import { memo, useRef } from "react";
import Grid from "@mui/material/Grid";
import {
  minLength,
  maxLength,
  minValue,
  number,
  required,
  TextInput,
  useDataProvider,
} from "react-admin";
import MuiDatePickerInput from "../../../common/MuiDatePickerInput";

import { toCurrency } from "../../../../utils/formatter";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormContext } from "react-hook-form";
import parserFormatNumInput from "../../../../utils/parserFormatNumInput";
import PurchaseOrderReference from "../../common/PurchaseOrderReference";
import CalculationResult from "../../common/CalculationResult";
import useEnterToAddRowAndFocus from "../../../../utils/useEnterToAddRowAndFocus"; // ✅ import

const equalOrLessThanDebt = (value, allValues, elm) => {
  const splite = elm.source.split(".");
  const debt = allValues[splite[0]][+splite[1]].debt;
  if (+value > +debt) {
    return (
      "Must be equal or less than " + toCurrency(false).format(debt) + " (debt)"
    );
  }
  return undefined;
};

const equalOrLessThanTotal = (value, allValues, elm) => {
  let remain = allValues.total;
  let count = 0;
  const splite = elm.source.split(".");
  let skipProcessLast = 0;
  for (let i = 0; i < allValues.customer_debt_correction_items.length; i++) {
    const elm = allValues.customer_debt_correction_items[i];
    if (+splite[1] === i) {
      skipProcessLast = elm.amount;
      continue;
    }
    count += +elm.amount;
    if (+count > +allValues.total) {
      return (
        "Must be equal or less than " +
        toCurrency(false).format(remain) +
        " (remaining of total correction)"
      );
    }
    remain -= +elm.amount;
  }
  count += +skipProcessLast;
  if (+count > +allValues.total) {
    return (
      "Must be equal or less than " +
      toCurrency(false).format(remain) +
      " (remaining of total correction)"
    );
  }
  return undefined;
};

const uniqueItem = (value, allValues, elm) => {
  let count = 0;
  for (let i = 0; i < allValues.customer_debt_correction_items.length; i++) {
    const elm = allValues.customer_debt_correction_items[i];
    if (value === elm.sale_id) {
      if (count > 0) {
        return "server.forbidden_duplication"; // "Duplicate is not allowed";
      }
      count += 1;
    }
  }
  return undefined;
};

const validateCode = [required(), minLength(36), maxLength(36), uniqueItem];
const validateAmount = [
  required(),
  number(),
  minValue(1),
  equalOrLessThanDebt,
  equalOrLessThanTotal,
];

const CstInput = ({
  saleIdSource,
  formData,
  dueDateSource,
  debtSource,
  transactionDateSource,
  amountSource,
  addRow, // ✅ diteruskan dari ArrayInput
}) => {
  const { setValue } = useFormContext();
  const dataProvider = useDataProvider();

  const amountRef = useRef(null); // ✅ ref untuk field correction

  // 🔹 Enter di correction → tambah baris baru & fokus ke saleIdSource
  useEnterToAddRowAndFocus(amountRef, addRow, 'input[name*="sale_id"]');

  const onCodeChangeHandler = async (id) => {
    if (id.length !== 36) return;
    try {
      const { date, customer_debt } =
        await dataProvider.getListNoParams("sales/" + encodeURIComponent(id));
      setValue(transactionDateSource, date);
      setValue(dueDateSource, customer_debt.due_date);
      setValue(debtSource, customer_debt.remain);
    } catch (error) {}
  };

  return (
    <Grid container spacing={2} sx={{ "& .MuiGrid-item": { py: 0 }, mt: 0 }}>
      <Grid item xs={12} lg={5.4}>
        <PurchaseOrderReference
          source={saleIdSource}
          validate={validateCode}
          label="mine.label.sales_number"
          dependOn="customer_id"
          queryName="CustomerDebt.customer_id"
          resource="sales"
          reference="sales"
          defaultFilters={{
            "CustomerDebt.settled_date": "IS NULL",
            "sales.approved": 1,
          }}
          formData={formData}
          onChange={onCodeChangeHandler}
        />
      </Grid>
      <Grid item xs={12} lg={1.2}>
        <MuiDatePickerInput
          variant="outlined"
          source={transactionDateSource}
          label="mine.label.transaction_date"
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={1.2}>
        <MuiDatePickerInput
          variant="outlined"
          source={dueDateSource}
          label="mine.label.due_date"
          disabled
          fullWidth
        />
      </Grid>
      <Grid item xs={12} lg={1.4}>
        <TextInput
          variant="outlined"
          source={debtSource}
          label="mine.label.customer_debt"
          fullWidth
          disabled
          InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
          {...parserFormatNumInput}
        />
      </Grid>
      <Grid item xs={12} lg={1.4} ref={amountRef}>
        <TextInput
          variant="outlined"
          source={amountSource}
          label="mine.label.correction"
          fullWidth
          validate={validateAmount}
          InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
          {...parserFormatNumInput}
        />
      </Grid>
      <Grid item xs={12} lg={1.4}>
        <CalculationResult
          variant="outlined"
          label="mine.label.remain"
          fullWidth
          dependOn={[debtSource, amountSource, "customer_debt_correction_journals.0.debit"]}
          InputProps={{ startAdornment: <InputAdornment position="start">Rp</InputAdornment> }}
          {...parserFormatNumInput}
        />
      </Grid>
    </Grid>
  );
};

const propsAreEqual = (prevProps, nextProps) => {
  return prevProps.amount === nextProps.amount && prevProps.sale_id === nextProps.sale_id;
};

const Beruk = memo(CstInput, propsAreEqual);

const Inputs = ({ formData, scopedFormData, getSource, addRow }) => {
  return (
    <Beruk
      {...scopedFormData}
      formData={formData}
      saleIdSource={getSource("sale_id")}
      transactionDateSource={getSource("transaction_date")}
      dueDateSource={getSource("due_date")}
      debtSource={getSource("debt")}
      amountSource={getSource("amount")}
      addRow={addRow} // ✅ diteruskan
    />
  );
};

export default Inputs;
